import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabaseClient';

const THEME_INJECT = `
  <script>
    (function() {
      var theme;
      try { theme = window.localStorage.getItem('ss_theme'); } catch(e) {}
      if (theme === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
      window.addEventListener('storage', function(e) {
        if (e.key === 'ss_theme') {
          if (e.newValue === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
          else document.documentElement.removeAttribute('data-theme');
        }
      });
    })();
  </script>
  <style>
    .topbar,.topnav,.topnav-wrap{display:none!important}
    html[data-theme="dark"] body{background:#0f172a!important}
    html[data-theme="dark"] .page{background:#0f172a!important}
  </style>
`;

const STORAGE_BRIDGE = (tenantId, restaurantName, accessToken, routeBase) => `
  <script>
    (function () {
      var tenantId = ${JSON.stringify(tenantId)};
      var restaurantName = ${JSON.stringify(restaurantName || '')};
      var exempt = { ss_supabase_v1: true, ss_currency_v1: true };
      window.__SS_URL__ = ${JSON.stringify(SUPABASE_URL)};
      window.__SS_ANON_KEY__ = ${JSON.stringify(SUPABASE_ANON_KEY)};
      window.__SS_ACCESS_TOKEN__ = ${JSON.stringify(accessToken || '')};

      // Capture the REAL storage object and prototype methods
      // before any other script can interfere
      var realStore = window.localStorage;
      var _get    = Storage.prototype.getItem.bind(realStore);
      var _set    = Storage.prototype.setItem.bind(realStore);
      var _remove = Storage.prototype.removeItem.bind(realStore);
      var _key    = Storage.prototype.key.bind(realStore);
      var _clear  = Storage.prototype.clear.bind(realStore);

      function mapKey(key) {
        if (typeof key !== 'string') return key;
        if (!key.startsWith('ss_')) return key;
        if (exempt[key]) return key;
        return 'tenant:' + tenantId + ':' + key;
      }

      // Build a proxied localStorage object scoped to this tenant
      var proxied = {
        getItem:    function(key) { return _get(mapKey(key)); },
        setItem:    function(key, val) { _set(mapKey(key), val); },
        removeItem: function(key) { _remove(mapKey(key)); },
        key:        function(i) { return _key(i); },
        clear: function() {
          var keys = [];
          for (var i = 0; i < realStore.length; i++) {
            var k = _key(i);
            if (k && k.indexOf('tenant:' + tenantId + ':ss_') === 0) keys.push(k);
          }
          keys.forEach(function(k) { _remove(k); });
        },
        get length() { return realStore.length; }
      };

      // Override localStorage ONLY on this iframe's window (not the shared Storage object)
      // This prevents cross-iframe bleed when navigating between stores
      try {
        Object.defineProperty(window, 'localStorage', {
          get: function() { return proxied; },
          configurable: true
        });
      } catch(e) {
        // Fallback: patch instance methods directly
        realStore.getItem    = function(key) { return _get(mapKey(key)); };
        realStore.setItem    = function(key, val) { _set(mapKey(key), val); };
        realStore.removeItem = function(key) { _remove(mapKey(key)); };
      }

      window.__SS_TENANT_ID__ = tenantId;
      window.sessionStorage.setItem('ss_active_tenant', tenantId);

      // Seed store name — fill in if storeName is missing or empty
      if (restaurantName) {
        var storeKey = 'tenant:' + tenantId + ':ss_store_v1';
        var existing = _get(storeKey);
        if (!existing) {
          _set(storeKey, JSON.stringify({ storeName: restaurantName }));
        } else {
          try {
            var storeData = JSON.parse(existing);
            if (!storeData.storeName) {
              storeData.storeName = restaurantName;
              _set(storeKey, JSON.stringify(storeData));
            }
          } catch(e) {}
        }
      }

      var routeBase = ${JSON.stringify(routeBase || '/restaurant/' + tenantId)};
      window.__SS_ROUTE_BASE__ = routeBase;
      try {
        window.sessionStorage.setItem('ss_last_route_base', routeBase);
        _set('ss_last_route_base', routeBase);
      } catch (e) {}

      var routeMap = {
        '/':               routeBase,
        '/display.html':   routeBase + '/display',
        '/admin.html':     routeBase + '/admin',
        '/products.html':  routeBase + '/products',
        '/modifiers.html': routeBase + '/modifiers',
        '/store.html':     routeBase + '/store',
        '/style.html':     routeBase + '/style'
      };

      function htmlToRoute(href) {
        if (!href) return null;
        // Normalize: strip query/hash, lowercase, ensure leading slash
        var path = href.replace(/[?#].*$/, '').toLowerCase();
        if (path.charAt(0) !== '/') path = '/' + path;
        return routeMap[path] || null;
      }

      // Capture-phase delegation: intercepts ALL clicks before browser default
      document.addEventListener('click', function (event) {
        var el = event.target;
        while (el && el.tagName !== 'A') el = el.parentElement;
        if (!el) return;
        var href = el.getAttribute('href');
        var nextHref = htmlToRoute(href);
        if (!nextHref) return;
        event.preventDefault();
        event.stopPropagation();
        if (window.top) {
          window.top.postMessage({ type: 'ss_navigate', href: nextHref }, '*');
        }
      }, true);

    })();
  </script>
`;

const LegacyRestaurantPage = ({ fileName, title, session, isAdmin, routeBasePrefix, embedded = false, fullscreen = false }) => {
  const { restaurantId } = useParams();
  const [srcDoc, setSrcDoc] = useState('');
  const [restaurantName, setRestaurantName] = useState(null);
  const [hasAccess, setHasAccess] = useState(Boolean(isAdmin));
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    let active = true;

    supabase
      .from('restaurants')
      .select('name, store_owner_id')
      .eq('id', restaurantId)
      .single()
      .then(({ data, error }) => {
        if (!active) return;

        if (error || !data) {
          setHasAccess(false);
          setRestaurantName('');
          setAccessLoading(false);
          return;
        }

        const allowed = Boolean(isAdmin || data.store_owner_id === session?.user?.id);

        setHasAccess(allowed);
        setRestaurantName(data?.name || '');
        setAccessLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isAdmin, restaurantId, session?.user?.id]);

  // Sync store name changes back to Supabase when store.html saves ss_store_v1
  useEffect(() => {
    if (!restaurantId || !hasAccess) return;
    const tenantKey = `tenant:${restaurantId}:ss_store_v1`;

    const handleStorage = (e) => {
      if (e.key !== tenantKey || !e.newValue) return;
      try {
        const data = JSON.parse(e.newValue);
        const newName = (data.storeName || '').trim();
        if (!newName) return;
        supabase
          .from('restaurants')
          .update({ name: newName })
          .eq('id', restaurantId)
          .then(() => setRestaurantName(newName));
      } catch (_) {}
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [hasAccess, restaurantId]);

  const bridge = useMemo(
    () => STORAGE_BRIDGE(
      restaurantId,
      restaurantName,
      session?.access_token,
      routeBasePrefix ? `${routeBasePrefix}/${restaurantId}` : undefined,
    ),
    [restaurantId, restaurantName, routeBasePrefix, session?.access_token]
  );

  useEffect(() => {
    if (restaurantName === null || !hasAccess) return;
    let active = true;

    const loadHtml = async () => {
      const response = await fetch(`/${fileName}`);
      const html = await response.text();
      const nextHtml = html.includes('<head>')
        ? html.replace('<head>', `<head><base href="/">${THEME_INJECT}${bridge}`)
        : `${THEME_INJECT}${bridge}${html}`;

      if (active) setSrcDoc(nextHtml);
    };

    loadHtml();
    return () => { active = false; };
  }, [bridge, fileName, hasAccess, restaurantName]);

  if (accessLoading) {
    return <div style={{ padding: '24px' }}>Loading...</div>;
  }

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={{ height: fullscreen ? '100vh' : (embedded ? 'calc(100vh - 220px)' : '100vh'), minHeight: fullscreen ? '100vh' : (embedded ? '720px' : '100vh'), background: '#fff', borderRadius: embedded && !fullscreen ? '18px' : 0, overflow: 'hidden' }}>
      <iframe
        key={`${restaurantId}-${fileName}-${restaurantName}`}
        title={title}
        srcDoc={srcDoc}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
};

export default LegacyRestaurantPage;
